/**
 * VoiceForge inference endpoint — inbound query handoff / health probe.
 */

export type VoiceBindKind =
  | "openai-tts"
  | "openai-stt"
  | "gpt-sovits"
  | "comfy"
  | string;

export type VoiceEndpoint = {
  bindKind: VoiceBindKind;
  endpointUrl: string;
  label?: string;
  engineId?: string;
  sessionToken?: string;
  source: "paste" | "binder" | "query";
};

/** Top chrome: Suite generate. House chips pick Kokoro / Breeze. */
export type GenMode = "suite";

export const GEN_MODES: GenMode[] = ["suite"];

const ENDPOINT_KEY = "voiceforge.endpoint.v1";
const MODE_KEY = "voiceforge.genMode.v1";
const LOCAL_API_KEY = "voiceforge.localApi.v1";

const VALID_MODES = new Set<GenMode>(GEN_MODES);

const KNOWN_KINDS = new Set([
  "openai-tts",
  "openai-stt",
  "gpt-sovits",
  "comfy",
  "whisper",
]);

/** One-shot persisted-mode remap. Catalog tabs are gone — land on Suite. */
const LEGACY_MODE: Record<string, GenMode> = {
  vgpu: "suite",
  inference: "suite",
  binder: "suite",
  gpuforge: "suite",
  production: "suite",
  hf: "suite",
  cloudflare: "suite",
  civitai: "suite",
  forge_endpoint: "suite",
  local: "suite",
};

export function loadGenMode(): GenMode {
  try {
    const v = localStorage.getItem(MODE_KEY);
    if (!v) return "suite";
    const remapped = LEGACY_MODE[v];
    if (remapped) {
      try {
        localStorage.setItem(MODE_KEY, remapped);
      } catch {
        /* ignore */
      }
      return remapped;
    }
    if (VALID_MODES.has(v as GenMode)) return v as GenMode;
  } catch {
    /* ignore */
  }
  return "suite";
}

export function saveGenMode(mode: GenMode): void {
  try {
    localStorage.setItem(MODE_KEY, mode);
  } catch {
    /* ignore */
  }
}

export function isGpuCatalogMode(_mode: GenMode): boolean {
  return false;
}

export function bindKindForEngine(engine?: string | null, repoId?: string | null): VoiceBindKind {
  const hay = `${engine || ""} ${repoId || ""}`.toLowerCase();
  if (/whisper|openai-stt|speech-to-text|\basr\b/.test(hay) && !/\btts\b/.test(hay)) {
    return "openai-stt";
  }
  if (/sovits|gpt-sovits/.test(hay)) return "gpt-sovits";
  return "openai-tts";
}

export function modeLabel(mode: GenMode): string {
  return mode === "suite" ? "Suite" : mode;
}

export function loadEndpoint(): VoiceEndpoint | null {
  try {
    const raw = localStorage.getItem(ENDPOINT_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as VoiceEndpoint;
    if (!p?.endpointUrl || !p.bindKind) return null;
    return p;
  } catch {
    return null;
  }
}

export function saveEndpoint(ep: VoiceEndpoint | null): void {
  try {
    if (!ep) {
      localStorage.removeItem(ENDPOINT_KEY);
      return;
    }
    localStorage.setItem(ENDPOINT_KEY, JSON.stringify(ep));
  } catch {
    /* ignore */
  }
}

export function loadLocalApiUrl(): string {
  try {
    return localStorage.getItem(LOCAL_API_KEY) || "http://127.0.0.1:9880";
  } catch {
    return "http://127.0.0.1:9880";
  }
}

export function saveLocalApiUrl(url: string): void {
  try {
    localStorage.setItem(LOCAL_API_KEY, url.trim().replace(/\/$/, ""));
  } catch {
    /* ignore */
  }
}

/** Inbound `?endpoint=` handoff (operator / suite). Lands on Forge endpoints. */
export function consumeBinderQuery(): VoiceEndpoint | null {
  try {
    const params = new URLSearchParams(window.location.search);
    const endpoint = (params.get("endpoint") || params.get("endpointUrl") || "").trim();
    if (!endpoint) return null;
    let kind = (params.get("kind") || params.get("bindKind") || "openai-tts").toLowerCase();
    if (kind === "whisper" || kind === "stt") kind = "openai-stt";
    if (kind === "tts" || kind === "speech") kind = "openai-tts";
    if (kind === "sovits" || kind === "gpt_sovits" || kind === "gptsovits") kind = "gpt-sovits";
    if (!KNOWN_KINDS.has(kind)) {
      /* free-form engine ids */
    }
    const ep: VoiceEndpoint = {
      bindKind: kind,
      endpointUrl: endpoint.replace(/\/$/, ""),
      label: params.get("label") || "Forge endpoint",
      engineId: params.get("engine") || undefined,
      sessionToken: params.get("key") || params.get("token") || undefined,
      source: "query",
    };
    saveEndpoint(ep);
    saveGenMode("suite");
    params.delete("endpoint");
    params.delete("endpointUrl");
    params.delete("kind");
    params.delete("bindKind");
    params.delete("label");
    params.delete("engine");
    params.delete("key");
    params.delete("token");
    const qs = params.toString();
    const next = `${window.location.pathname}${qs ? `?${qs}` : ""}${window.location.hash || ""}`;
    window.history.replaceState(null, "", next);
    return ep;
  } catch {
    return null;
  }
}

export function normalizeEndpointUrl(url: string): string {
  return url.trim().replace(/\/$/, "");
}

export async function probeEndpointHealth(
  ep: VoiceEndpoint,
): Promise<{ ok: boolean; message: string }> {
  const base = normalizeEndpointUrl(ep.endpointUrl);
  try {
    let path = "/";
    if (ep.bindKind === "openai-tts" || ep.bindKind === "openai-stt") {
      path = "/v1/models";
    } else if (ep.bindKind === "gpt-sovits") {
      path = "/control?command=status";
    } else if (ep.bindKind === "comfy") {
      path = "/system_stats";
    }

    const headers: Record<string, string> = {};
    if (ep.sessionToken) headers.Authorization = `Bearer ${ep.sessionToken}`;
    const res = await fetch(`${base}${path}`, {
      method: "GET",
      mode: "cors",
      headers,
      signal: AbortSignal.timeout(8000),
    });
    if (res.ok || res.status === 401 || res.status === 403 || res.status === 405) {
      return { ok: true, message: `Reachable (${res.status})` };
    }
    if (ep.bindKind === "gpt-sovits" && res.status >= 400 && res.status < 500) {
      return { ok: true, message: `API up (${res.status})` };
    }
    return { ok: false, message: `HTTP ${res.status}` };
  } catch (e) {
    try {
      const res2 = await fetch(base, {
        method: "GET",
        mode: "cors",
        signal: AbortSignal.timeout(5000),
      });
      if (res2.ok || res2.status < 500) {
        return { ok: true, message: `Reachable (${res2.status})` };
      }
    } catch {
      /* fall through */
    }
    return { ok: false, message: e instanceof Error ? e.message : String(e) };
  }
}
